import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';
import {
  GenerationForecastDayResponse,
  PvActualDayResponse,
  fmtKwh,
  fmtInt,
  fmtDec,
} from '@/mocks/generationForecast';

function formatNumber(value: number | null | undefined, digits = 1, fallback = '\u2014'): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return fallback;
  return Number(value).toFixed(digits);
}

interface Props {
  dayData: GenerationForecastDayResponse | null;
  pvActualData: PvActualDayResponse | null;
  /** Погодинний тіньовий прогноз ECMWF — друга модель поруч із Base. */
  ecmwfShadowData?: { hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null;
  dayLabel: string;
  loading: boolean;
  onRefresh?: () => void;
}

type DeltaSeverity = 'zero_ok' | 'forecast_zero' | 'low' | 'high' | 'critical' | 'missing';

interface TableRow {
  hour: string;
  forecast: number | null;
  ecmwf: number | null;
  actual: number | null;
  deltaKw: number | null;
  deltaPct: number | null;
  deltaEcmwfKw: number | null;
  deltaEcmwfPct: number | null;
  deltaSeverity: DeltaSeverity;
  coverage: number | null;
  unit6Kw: number | null;
  shortwave: number | null;
  cloudCover: number | null;
  cloudCoverLow: number | null;
  cloudCoverMid: number | null;
  cloudCoverHigh: number | null;
  temperature: number | null;
  wind: number | null;
  precipitation: number | null;
  status: string;
  actualSamplePosition: string | null;
  actualIsFinal: boolean;
  actualStatus: string;
  readAtLocal: string | null;
  isMissing: boolean;
}

type SortKey = 'hour' | 'forecast' | 'ecmwf' | 'actual' | 'deltaKw' | 'deltaPct' | 'shortwave' | 'cloudCover' | 'cloudCoverBreakdown' | 'temperature';
type SortDir = 'asc' | 'desc';

function getDeltaSeverity(forecast: number | null, actual: number | null): DeltaSeverity {
  if (forecast == null || actual == null) return 'missing';
  const f = forecast;
  const a = actual;
  if (f === 0 && a === 0) return 'zero_ok';
  if (f === 0 && a > 0) return 'forecast_zero';
  const deltaPct = ((a - f) / f) * 100;
  const absPct = Math.abs(deltaPct);
  if (absPct <= 10) return 'low';
  if (absPct <= 35) return 'high';
  return 'critical';
}

// Колірна догма: червоний = ТІЛЬКИ критична помилка; попередження → бурштин (warn);
// точна година (Низьке) та крайові випадки → нейтральний сірий. Пілюлі — контурні,
// кольори theme-aware (видимі і на світлому, і на темному тлі).
function getSeverityMeta(sev: DeltaSeverity, C: ReturnType<typeof tableColors>) {
  switch (sev) {
    case 'zero_ok':
      return { labelKey: 'fc_sev_zero_ok', color: C.dim };
    case 'forecast_zero':
      return { labelKey: 'fc_sev_f0', color: C.dim };
    case 'low':
      return { labelKey: 'fc_sev_low', color: C.muted };
    case 'high':
      return { labelKey: 'fc_sev_high', color: C.warn };
    case 'critical':
      return { labelKey: 'fc_sev_critical', color: C.err };
    default:
      return { labelKey: null as string | null, color: C.dim };
  }
}


// Кольори таблиці відхилень на дизайн-токенах brandbook (світла/темна).
function tableColors(isDark: boolean) {
  return isDark ? {
    card: '#2F3A45', line: 'rgba(255,255,255,0.08)',
    text: '#F2F2F2', muted: '#8A9199', dim: '#5E656D',
    base: '#D23538', actual: '#F2F2F2', pos: '#4CAF6A', neg: '#E5484D',
    ecmwf: '#4AA8C0',   // друга модель — приглушений блакитний (як на графіку)
    ok: '#4CAF6A', warn: '#E0A24A', err: '#E5484D',
    zebra: 'rgba(255,255,255,0.015)',
    shadow: '0 8px 24px rgba(0,0,0,0.30)',
  } : {
    card: '#FFFFFF', line: '#E4E5E7',
    text: '#28323C', muted: '#5E656D', dim: '#9AA0A6',
    base: '#CC181B', actual: '#28323C', pos: '#2E7D46', neg: '#CC181B',
    ecmwf: '#2B7C93',   // друга модель — приглушений блакитний (як на графіку)
    ok: '#2E7D46', warn: '#C77A1B', err: '#CC181B',
    zebra: 'rgba(40,50,60,0.025)',
    shadow: '0 8px 24px rgba(40,50,60,0.08)',
  };
}

export default function DeviationTable({ dayData, pvActualData, ecmwfShadowData, dayLabel, loading, onRefresh }: Props) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const C = tableColors(isDark);
  const [sortKey, setSortKey] = useState<SortKey>('hour');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const rows = (() => {
    const hourly = dayData?.hourly ?? [];
    const actualMap = new Map<string, PvActualDayResponse['actual'][number]>();
    if (pvActualData?.actual?.length) {
      for (const a of pvActualData.actual) {
        actualMap.set(a.hour, a);
      }
    }

    const ecmwfMap = new Map<string, number | null>();
    for (const e of ecmwfShadowData?.hourly ?? []) ecmwfMap.set(e.hour, e.forecastPowerKw ?? null);

    const mapped: TableRow[] = hourly.map((h) => {
      const act = actualMap.get(h.hour);
      const forecast = h.forecastEnergyKwh;
      const ecmwf = ecmwfMap.get(h.hour) ?? null;
      const actual = act?.actualPacKw ?? null;
      const deltaKw = forecast != null && actual != null ? actual - forecast : null;
      const deltaPct = forecast != null && actual != null && forecast > 0 ? ((actual - forecast) / forecast) * 100 : null;
      // Те саме відхилення, але проти ECMWF — друга модель оцінюється тим же способом.
      const deltaEcmwfKw = ecmwf != null && actual != null ? actual - ecmwf : null;
      const deltaEcmwfPct = ecmwf != null && actual != null && ecmwf > 0 ? ((actual - ecmwf) / ecmwf) * 100 : null;
      const sev = getDeltaSeverity(forecast, actual);

      return {
        hour: h.hour,
        forecast,
        ecmwf,
        actual,
        deltaKw,
        deltaPct,
        deltaEcmwfKw,
        deltaEcmwfPct,
        deltaSeverity: sev,
        coverage: act?.coverage ?? null,
        unit6Kw: act?.unit6Kw ?? null,
        shortwave: h.weather?.shortwaveRadiation ?? null,
        cloudCover: h.weather?.cloudCover ?? null,
        cloudCoverLow: h.weather?.cloudCoverLow ?? null,
        cloudCoverMid: h.weather?.cloudCoverMid ?? null,
        cloudCoverHigh: h.weather?.cloudCoverHigh ?? null,
        temperature: h.weather?.temperature2m ?? null,
        wind: h.weather?.windSpeed10m ?? null,
        precipitation: h.weather?.precipitation ?? null,
        status: h.status,
        actualSamplePosition: act?.samplePosition ?? null,
        actualIsFinal: act?.isFinal ?? false,
        actualStatus: act?.status ?? 'future',
        readAtLocal: act?.readAtLocal ?? null,
        isMissing: h.status === 'missing' || h.forecastEnergyKwh == null,
      };
    });

    // Add any actual-only rows not in forecast
    const forecastHours = new Set(hourly.map((h) => h.hour));
    if (pvActualData?.actual?.length) {
      for (const a of pvActualData.actual) {
        if (!forecastHours.has(a.hour)) {
          const actual = a.actualPacKw ?? null;
          const sev = getDeltaSeverity(null, actual);
          const ecmwfOnly = ecmwfMap.get(a.hour) ?? null;
          mapped.push({
            hour: a.hour,
            forecast: null,
            ecmwf: ecmwfOnly,
            actual,
            deltaKw: null,
            deltaPct: null,
            deltaEcmwfKw: ecmwfOnly != null && actual != null ? actual - ecmwfOnly : null,
            deltaEcmwfPct: ecmwfOnly != null && actual != null && ecmwfOnly > 0
              ? ((actual - ecmwfOnly) / ecmwfOnly) * 100 : null,
            deltaSeverity: sev,
            coverage: a.coverage ?? null,
            unit6Kw: a.unit6Kw ?? null,
            shortwave: null,
            cloudCover: null,
            cloudCoverLow: null,
            cloudCoverMid: null,
            cloudCoverHigh: null,
            temperature: null,
            wind: null,
            precipitation: null,
            status: 'actual_only',
            actualSamplePosition: a.samplePosition ?? null,
            actualIsFinal: a.isFinal,
            actualStatus: a.status,
            readAtLocal: a.readAtLocal ?? null,
            isMissing: true,
          });
        }
      }
    }

    const filtered = showOnlyActive
      ? mapped.filter((r) => !r.isMissing && (r.forecast ?? 0) > 0)
      : mapped;

    return filtered.sort((a, b) => {
      const getVal = (r: TableRow, k: SortKey) => {
        if (k === 'hour') return r.hour;
        if (k === 'forecast') return r.forecast ?? -1;
        if (k === 'actual') return r.actual ?? -1;
        if (k === 'deltaKw') return r.deltaKw ?? -999999;
        if (k === 'deltaPct') return r.deltaPct ?? -999999;
        if (k === 'shortwave') return r.shortwave ?? -1;
        if (k === 'cloudCover') return r.cloudCover ?? -1;
        if (k === 'cloudCoverBreakdown') return ((r.cloudCoverLow ?? 0) + (r.cloudCoverMid ?? 0) + (r.cloudCoverHigh ?? 0)) || -1;
        return r.temperature ?? -999;
      };
      const av = getVal(a, sortKey);
      const bv = getVal(b, sortKey);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  // Рядок «Підсумок за добу» прибрано з таблиці 24.09: накопичені суми на
  // поточну годину тепер стоять на KPI-плитках угорі сторінки (Факт / Base /
  // ECMWF), і два місця з тими самими числами лише конкурували між собою.
  const nowChips = (() => {
    const nowKey = new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Kyiv' });
    const curHour = nowKey.slice(0, 2) + ':00';
    const baseNow = (dayData?.hourly ?? []).find((h) => h.hour === curHour)?.forecastEnergyKwh ?? null;
    const actualNow = (pvActualData?.actual ?? []).find((a) => a.hour === curHour)?.actualPacKw ?? null;
    const deltaKw = baseNow != null && actualNow != null ? actualNow - baseNow : null;
    const deltaPct = baseNow != null && actualNow != null && baseNow > 0 ? ((actualNow - baseNow) / baseNow) * 100 : null;
    const deltaColor = deltaKw == null ? C.muted : deltaKw > 0 ? C.pos : deltaKw < 0 ? C.neg : C.text;
    return [
      { label: 'Base Forecast', value: baseNow == null ? String.fromCharCode(8212) : fmtKwh(baseNow) + ' kWh', color: C.base },
      { label: t('fc_series_actual'), value: actualNow == null ? String.fromCharCode(8212) : fmtKwh(actualNow) + ' kWh', color: C.actual },
      { label: 'Δ', value: deltaKw == null ? String.fromCharCode(8212) : (deltaKw > 0 ? '+' : '') + fmtKwh(deltaKw) + ' ' + t('ak_fc_kwh') + (deltaPct != null ? ' / ' + (deltaPct > 0 ? '+' : '') + fmtDec(deltaPct, 1) + '%' : ''), color: deltaColor },
    ];
  })();

  // Повнодобові числа — окремо від рядка підсумку: вони не належать жодній
  // колонці (підсумок рахується за зіставні години, а це вся доба).
  // «Факт за добу» беремо готовим від бекенду: actualPacKw — миттєвий замір
  // потужності, тож його сума лише наближає енергію, і бекенд сам позначає
  // свій підсумок як приблизний (totalActualKwhApprox).
  // Прогноз на повну добу переїхав у сам рядок підсумку (вільна колонка
  // «Короткохв.»), тож тут лишається тільки факт за добу.
  const dayChips = pvActualData?.totalActualKwhApprox != null
    ? [{ label: t('fc_sum_day_actual'), value: fmtKwh(pvActualData.totalActualKwhApprox) + ' ' + t('ak_fc_kwh_2'), color: C.actual }]
    : [];


  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1 opacity-40">
      <i className={`ri-arrow-up-s-line text-[10px] leading-none ${sortKey === col && sortDir === 'asc' ? 'opacity-100' : ''}`}></i>
      <i className={`ri-arrow-down-s-line text-[10px] leading-none -mt-1 ${sortKey === col && sortDir === 'desc' ? 'opacity-100' : ''}`}></i>
    </span>
  );

  if (loading) {
    return (
      <div
        className="rounded-xl p-8 flex items-center justify-center"
        style={{ background: C.card, border: '1px solid ' + C.line, boxShadow: C.shadow, borderRadius: 14, minHeight: 240 }}
      >
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: C.muted }}></i>
          <p className="text-sm mt-3" style={{ color: C.muted }}>{t('fc_loading_hourly')}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl"
      style={{ background: C.card, border: '1px solid ' + C.line, boxShadow: C.shadow, borderRadius: 14 }}
    >
      {/* Header */}
      <div className="flex items-start justify-between px-4 md:px-5 py-4" style={{ borderBottom: '1px solid ' + C.line }}>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="text-ink font-semibold text-base" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>{t('fc_dev_title')}</div>
          <div className="flex items-center gap-2 flex-wrap">
            {[...nowChips, ...dayChips].map((c) => (
              <div
                key={c.label}
                className="flex items-center justify-center gap-1.5 rounded-md text-xs font-medium whitespace-nowrap"
                style={{ minWidth: 140, padding: '6px 14px', background: c.color + '1F', border: '1px solid ' + c.color + '55' }}
              >
                <div className="w-2 h-2 rounded-full" style={{ background: c.color }}></div>
                <span style={{ color: C.muted }}>{c.label}</span>
                <span className="font-semibold" style={{ color: c.color }}>{c.value}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => setShowOnlyActive((v) => !v)}
          className="text-xs px-3 py-1.5 rounded-lg cursor-pointer transition-all whitespace-nowrap"
          style={{
            background: showOnlyActive ? C.ok + '14' : 'transparent',
            color: showOnlyActive ? C.ok : C.muted,
            border: '1px solid ' + (showOnlyActive ? C.ok + '55' : C.line),
          }}
        >
          {showOnlyActive ? t('fc_active_only') : t('fc_all_hours')}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs md:text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid ' + C.line }}>
              {([
                ['hour', t('fc_col_hour')],
                ['forecast', 'Base, kWh'],
                ['ecmwf', 'ECMWF, kWh'],
                ['actual', t('fc_col_actual_88')],
                ['deltaKw', 'Δ Base/ECMWF, kWh'],
                ['deltaPct', 'Base/ECMWF Δ, %'],
                ['shortwave', t('fc_col_shortwave')],
                ['cloudCover', t('fc_col_cloud')],
                ['cloudCoverBreakdown', t('fc_col_cloud_lmh')],
                ['temperature', t('fc_col_temp')],
              ] as [SortKey, string][]).map(([key, label]) => (
                <th
                  key={key}
                  onClick={() => handleSort(key)}
                  className="text-left px-2 md:px-3 py-2 md:py-3 font-medium cursor-pointer select-none whitespace-nowrap"
                  style={{ color: sortKey === key ? C.base : C.muted, fontFamily: 'Urbanist, Inter, sans-serif', fontSize: 10 }}
                >
                  {label}<SortIcon col={key} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((h, idx) => {
              const mainColor = h.isMissing ? C.dim : C.text;
              const secondaryColor = h.isMissing ? C.dim : C.muted;
              const sev = getSeverityMeta(h.deltaSeverity, C);
              const deltaColor =
                h.deltaKw == null
                  ? C.dim
                  : h.deltaKw > 0
                    ? C.actual
                    : h.deltaKw < 0
                      ? C.base
                      : C.text;
              const deltaPctColor =
                h.deltaPct == null
                  ? C.dim
                  : h.deltaPct > 0
                    ? C.actual
                    : h.deltaPct < 0
                      ? C.base
                      : C.text;
              const deltaEcmwfColor = h.deltaEcmwfKw == null ? C.dim
                : h.deltaEcmwfKw > 0 ? C.actual : h.deltaEcmwfKw < 0 ? C.ecmwf : C.text;
              const deltaEcmwfPctColor = h.deltaEcmwfPct == null ? C.dim
                : h.deltaEcmwfPct > 0 ? C.actual : h.deltaEcmwfPct < 0 ? C.ecmwf : C.text;

              return (
                <tr
                  key={h.hour}
                  style={{
                    borderBottom: '1px solid ' + C.line,
                    background: idx % 2 === 0 ? 'transparent' : C.zebra,
                  }}
                >
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm font-medium" style={{ color: mainColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.hour}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: h.forecast == null ? C.dim : C.base, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.forecast == null ? '—' : fmtKwh(h.forecast)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: h.ecmwf == null ? C.dim : C.ecmwf, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.ecmwf == null ? '—' : fmtKwh(h.ecmwf)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: h.actual == null ? C.dim : C.actual, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.actual == null ? '—' : fmtKwh(h.actual)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <span style={{ color: deltaColor }}>
                      {h.deltaKw == null ? '—' : `${h.deltaKw > 0 ? '+' : ''}${fmtKwh(h.deltaKw)}`}
                    </span>
                    <span style={{ color: C.dim }}>{' / '}</span>
                    <span style={{ color: deltaEcmwfColor }}>
                      {h.deltaEcmwfKw == null ? '—' : `${h.deltaEcmwfKw > 0 ? '+' : ''}${fmtKwh(h.deltaEcmwfKw)}`}
                    </span>
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <span style={{ color: deltaPctColor }}>
                      {h.deltaPct == null ? '—' : `${h.deltaPct > 0 ? '+' : ''}${fmtDec(h.deltaPct, 1)}%`}
                    </span>
                    <span style={{ color: C.dim }}>{' / '}</span>
                    <span style={{ color: deltaEcmwfPctColor }}>
                      {h.deltaEcmwfPct == null ? '—' : `${h.deltaEcmwfPct > 0 ? '+' : ''}${fmtDec(h.deltaEcmwfPct, 1)}%`}
                    </span>
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: secondaryColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.shortwave == null ? '—' : fmtInt(h.shortwave)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: secondaryColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.cloudCover == null ? '—' : fmtDec(h.cloudCover, 1)}
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono" style={{ color: secondaryColor, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                    <span style={{ color: '#FCA5A5' }}>{h.cloudCoverLow == null ? '–' : Math.round(h.cloudCoverLow)}</span>
                    {' / '}
                    <span style={{ color: '#FCD34D' }}>{h.cloudCoverMid == null ? '–' : Math.round(h.cloudCoverMid)}</span>
                    {' / '}
                    <span style={{ color: '#93C5FD' }}>{h.cloudCoverHigh == null ? '–' : Math.round(h.cloudCoverHigh)}</span>
                  </td>
                  <td className="px-2 md:px-3 py-2 md:py-3 font-mono text-xs md:text-sm" style={{ color: secondaryColor, fontFamily: 'JetBrains Mono, monospace' }}>
                    {h.temperature == null ? '—' : fmtDec(h.temperature, 1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}